/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Related-article selection for the kennisbank. Pure and fully deterministic
 * on purpose: the article page renders this during SSR (prerender seeds the
 * full index) and again on hydration — any nondeterminism would be a React 19
 * hydration mismatch. Scores candidates by keyword overlap with the current
 * article (title words weigh double), falls back to newest-first when nothing
 * overlaps.
 */

export interface RelatedCandidate {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  heroImage?: string;
  // Rides along untouched: the selection returns the candidates themselves, so
  // the related card crops the hero where the editor positioned it.
  heroImageFocus?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Tiny stopword sets — payloads are already localized, so we apply the union
// rather than threading a language parameter through.
const STOPWORDS = new Set([
  // NL
  'de',
  'het',
  'een',
  'en',
  'van',
  'voor',
  'met',
  'aan',
  'bij',
  'dat',
  'die',
  'deze',
  'je',
  'jouw',
  'wat',
  'hoe',
  'zijn',
  'is',
  'in',
  'op',
  'niet',
  'ook',
  'naar',
  'wordt',
  'als',
  'per',
  'over',
  // EN
  'the',
  'a',
  'an',
  'and',
  'of',
  'for',
  'with',
  'to',
  'at',
  'that',
  'this',
  'your',
  'what',
  'how',
  'are',
  'on',
  'not',
  'be',
  'from',
  'also',
  'you',
  'when',
  'do',
  'does',
  'it',
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3 && !STOPWORDS.has(w));
}

function candidateDate(c: { createdAt?: string; updatedAt?: string }): number {
  const ts = Date.parse(c.updatedAt || c.createdAt || '');
  return Number.isNaN(ts) ? 0 : ts;
}

/**
 * Pick the `limit` most related candidates for `current`.
 *
 * Scoring: build a weight map from the current article (title tokens ×2,
 * excerpt tokens ×1); a candidate scores the summed weights of the current
 * article's tokens that appear in its own title+excerpt token set. Sort:
 * score desc → newest (updatedAt/createdAt) desc → slug asc. The date and
 * slug tiebreaks make the result deterministic AND give a sensible
 * newest-first order for corpora with zero overlap.
 */
export function selectRelatedArticles<T extends RelatedCandidate>(
  current: { id?: string; slug: string; title?: string; excerpt?: string },
  candidates: T[],
  limit = 3
): T[] {
  const weights = new Map<string, number>();
  for (const w of tokenize(current.title ?? '')) weights.set(w, (weights.get(w) ?? 0) + 2);
  for (const w of tokenize(current.excerpt ?? '')) weights.set(w, (weights.get(w) ?? 0) + 1);

  return candidates
    .filter(
      (c) =>
        c.slug !== current.slug &&
        (current.id === undefined || c.id !== current.id) &&
        typeof c.title === 'string' &&
        c.title.trim() !== ''
    )
    .map((c) => {
      const own = new Set(tokenize(`${c.title} ${c.excerpt ?? ''}`));
      let score = 0;
      for (const w of own) score += weights.get(w) ?? 0;
      return { c, score };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const dateDiff = candidateDate(b.c) - candidateDate(a.c);
      if (dateDiff !== 0) return dateDiff;
      return a.c.slug < b.c.slug ? -1 : a.c.slug > b.c.slug ? 1 : 0;
    })
    .slice(0, limit)
    .map((s) => s.c);
}
